import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Ban,
  BadgeCheck,
  Bot,
  ChevronLeft,
  Crown,
  MessagesSquare,
  MonitorSmartphone,
  ShieldCheck,
  Snowflake,
} from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchUser, banUser, unbanUser, freezeUser, unfreezeUser, verifyUser, type AdminUserDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, PIIField, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { DangerDialog } from '@/components/DangerDialog'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * A3.07 — картка користувача. GET /admin/users/:id. PII (телефон) замасковано.
 * Бан/заморозка/верифікація — реальні дії (EventFlow command bus, AdminServer).
 * Без GDPR-експорту/нотаток/страйків/ризик-скору/тікетів — цього в бекенді немає.
 */
export function UserCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [banOpen, setBanOpen] = useState(false)
  const [freezeOpen, setFreezeOpen] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchUser(Number(id), ac.signal)
      .then((u) => {
        if (alive) setUser(u)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const name = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || '—'
    : ''

  const runSimple = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      toast(t.users.actionDone, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    }
  }

  const canModerate = can(PERMISSIONS.USERS_MODERATE)

  const actions = (
    <div className="flex flex-wrap items-center gap-1.5">
      {canModerate && user && (
        <>
          {user.is_banned ? (
            <button
              type="button"
              onClick={() => void runSimple(() => unbanUser(user.id))}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldCheck size={13} />
              {t.users.unban}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setBanOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-2.5 py-1 text-sm text-danger hover:border-danger"
            >
              <Ban size={13} />
              {t.users.ban}
            </button>
          )}
          {user.is_frozen ? (
            <button
              type="button"
              onClick={() => void runSimple(() => unfreezeUser(user.id))}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldCheck size={13} />
              {t.users.unfreeze}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setFreezeOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-warning bg-warning-subtle px-2.5 py-1 text-sm text-warning hover:border-warning"
            >
              <Snowflake size={13} />
              {t.users.freeze}
            </button>
          )}
          <button
            type="button"
            onClick={() => void runSimple(() => verifyUser(user.id, !user.is_official))}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <BadgeCheck size={13} />
            {user.is_official ? t.users.unverify : t.users.verify}
          </button>
        </>
      )}
      <Link
        to={`/users/${id}/sessions`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <MonitorSmartphone size={13} />
        {t.users.sessions}
      </Link>
      <Link
        to={`/users/${id}/groups`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <MessagesSquare size={13} />
        {t.users.groups}
      </Link>
      <Link
        to={`/users/${id}/owned-groups`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Crown size={13} />
        {t.users.ownedGroups}
      </Link>
      <Link
        to={`/users/${id}/bots`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Bot size={13} />
        {t.userExtra.botsTitle}
      </Link>
      <Link
        to="/users"
        className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={13} />
        {t.nav.users}
      </Link>
    </div>
  )

  return (
    <>
      <PageHeader
        title={user ? name || (user.username ? `@${user.username}` : `#${user.id}`) : t.users.cardTitle}
        screenId="A3.07"
        actions={actions}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !user ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3 flex items-center gap-2">
              <StatusBadge status={user.status === 'banned' ? 'banned' : 'active'} />
              {user.is_frozen && <Tag>{t.users.freeze}</Tag>}
              {user.is_bot && <Tag>bot</Tag>}
              {user.is_official && <Tag>official</Tag>}
            </div>
            <Field label={t.col.id}>
              <MonoValue value={String(user.id)} />
            </Field>
            <Field label={t.users.userNumber}>
              {user.user_number ? <MonoValue value={user.user_number} /> : '—'}
            </Field>
            <Field label={t.col.name}>{name}</Field>
            <Field label={t.col.username}>{user.username ? `@${user.username}` : '—'}</Field>
            <Field label={t.col.phone}>
              {user.phone ? <PIIField value={user.phone} level="L2" /> : '—'}
            </Field>
            <Field label={t.users.bio}>{user.bio || '—'}</Field>
            {user.is_banned && (
              <Field label={t.users.bannedReason}>
                <span className="text-danger">{user.ban_reason || '—'}</span>
              </Field>
            )}
            {user.is_frozen && (
              <Field label={t.users.freezeReason}>
                <span className="text-warning">{user.freeze_reason || '—'}</span>
              </Field>
            )}
            <Field label={t.col.registered}>
              {user.created_at ? <RelativeTime value={user.created_at} /> : '—'}
            </Field>
            <Field label={t.users.lastSeen}>
              {user.last_seen ? <RelativeTime value={user.last_seen} /> : '—'}
            </Field>
          </div>
        )}
      </div>

      {banOpen && user && (
        <DangerDialog
          open
          onClose={() => setBanOpen(false)}
          screenId="A3.27"
          level={2}
          title={t.users.banTitle(name)}
          description={t.users.banDesc}
          confirmLabel={t.users.ban}
          onConfirm={async ({ reason }) => {
            try {
              await banUser(user.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}

      {freezeOpen && user && (
        <DangerDialog
          open
          onClose={() => setFreezeOpen(false)}
          screenId="A3.27"
          level={2}
          title={t.users.freezeTitle(name)}
          description={t.users.freezeDesc}
          confirmLabel={t.users.freeze}
          onConfirm={async ({ reason }) => {
            try {
              await freezeUser(user.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReload((n) => n + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
